import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DataSecurityWithAllFeaturesAndRanksTct } from '@dealstack/shared/lib/generated';

import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getDealSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { makeSecurityPillOptions } from '@dealstack/frontend/shared/incomeRights.ts';
import {
	SecurityTrackingFormDto,
	SecurityTrackingShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRightsTracking/incomeRightsTracking.zod.ts';

export enum TRACKING_FORM_FIELD {
	trackedSecurities = 'trackedSecurities',
}

export const useIncomeRightsTrackingController = (
	security: DataSecurityWithAllFeaturesAndRanksTct | null
) => {
	const getDealSecuritiesRequest = useQuery({
		queryKey: [API_PATHS.SECURITY_DEAL_GET],
		queryFn: () => getDealSecurities(API_PATHS.SECURITY_DEAL_GET),
	});

	const dealSecurities = useMemo(
		() =>
			makeSecurityPillOptions(
				getDealSecuritiesRequest?.data?.filter(
					(sec) => sec.id !== security?.id && sec.issuerId !== security?.issuerId
				) || []
			),
		[getDealSecuritiesRequest?.data, security?.id, security?.issuerId]
	);
	const trackedSecurityIds = useMemo(
		() => security?.tracking?.map((trackingSecurity) => trackingSecurity.securityId) || [],
		[security?.tracking]
	);

	const trackingFormDefaultValues = useMemo(
		() =>
			({
				trackedSecurities: trackedSecurityIds,
			}) as SecurityTrackingFormDto,
		[trackedSecurityIds]
	);

	const form = useForm<SecurityTrackingFormDto>({
		mode: 'all',
		resolver: zodResolver(SecurityTrackingShape()),
		defaultValues: trackingFormDefaultValues,
		shouldFocusError: false,
	});

	useEffect(() => {
		if (trackedSecurityIds.length) {
			void form.reset(trackingFormDefaultValues);
		}
	}, [trackedSecurityIds]);

	const resetForm = () => {
		form.reset(trackingFormDefaultValues);
	};

	return {
		form,
		dealSecurities,
		resetForm,
	};
};
